import type { Vec2, Vec3, Ring } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { transformDirection, transformPoint } from '../mesh/meshBuilder';
import { ensureCCW } from '../../geo/polygon';
import { panelPoint, panelUv, verticalPanel, type Panel } from './panels';
import {
  holeOf,
  meshGableWall,
  meshRectWall,
  type Fan,
  type Hole,
  type Pane,
  type WallMesh,
} from './wallMesh';
import { triangulateRing, orientedFace } from './solids';
import type { HouseOpenings, Opening } from './openings';
import type { DetailKind, DetailSink } from './detailSink';
import type { GableOutline } from './gables';
import { leadedLight, leadedStyle, lightUv } from './glazing';

/** The house body in its frame: x from the street front inward, z to the right, y up. */
export interface Body {
  /** Where the body's front wall stands; a stepped or spout slab stands in front of it. */
  readonly x0: number;
  readonly depth: number;
  readonly half: number;
  readonly eaves: number;
  readonly ridge: number;
}

export interface FacadeStyles {
  readonly wall: VertexStyle;
  /** Glass and the dark interior behind open lights. */
  readonly glass: VertexStyle;
  /** Door and loft-door leaves. */
  readonly leaf: VertexStyle;
}

export interface FacadeContext {
  readonly openFrontDoor?: boolean | undefined;
  readonly builder: MeshBuilder;
  readonly styles: FacadeStyles;
  readonly frame: Frame;
  readonly details: DetailSink;
  readonly edgeId: number;
}

/** A wall plane: panel coordinates across and up it, and the direction it faces. */
export interface WallFace {
  readonly panel: Panel;
  readonly outward: Vec3;
}

export function wallFace(origin: Vec3, outward: Vec3): WallFace {
  return { panel: verticalPanel(origin, outward), outward };
}

function emitFans(ctx: FacadeContext, panel: Panel, fans: readonly Fan[]): void {
  for (const fan of fans) {
    const points = fan.map((p) => panelPoint(panel, p[0], p[1]));
    ctx.builder.polygon(ctx.styles.wall, ctx.frame, points, panelUv(panel));
  }
}

type Corners = [Vec2, Vec2, Vec2, Vec2];

function mapCorners(c: Corners, f: (x: number, y: number) => Vec2): Corners {
  return [f(...c[0]), f(...c[1]), f(...c[2]), f(...c[3])];
}

/**
 * A pane's style and coordinates. Door leaves carry their planks upright, so their plank coordinate
 * runs along the wall; leaded lights run theirs from the light's lower-left corner (STYLE §15.1).
 */
function paneFace(
  ctx: FacadeContext,
  opening: Opening,
  slice: number,
  corners: Corners
): { style: VertexStyle; uv: Corners } {
  if (opening.kind === 'loftDoor' || (opening.kind === 'door' && slice === 0)) {
    return { style: ctx.styles.leaf, uv: mapCorners(corners, (x, y) => [y, x]) };
  }
  const light = leadedLight(opening, slice);
  if (!light) return { style: ctx.styles.glass, uv: corners };
  return {
    style: leadedStyle(ctx.styles.glass, light),
    uv: mapCorners(corners, (x, y) => lightUv(light, x, y)),
  };
}

/* A shop opening stands open onto its front room (shopRoom.ts), so it takes no pane. */
function emitPanes(
  ctx: FacadeContext,
  panel: Panel,
  panes: readonly Pane[],
  openings: readonly Opening[],
  outward: Vec3
): void {
  for (const { hole, slice, x0, y0, x1, y1 } of panes) {
    if (openings[hole]!.kind === 'shop') continue;
    if (ctx.openFrontDoor && outward[0] < -0.99 && openings[hole]!.kind === 'door' && openings[hole]!.y < 0.1 && slice === 0) continue;
    const corners: Corners = [
      [x0, y0],
      [x1, y0],
      [x1, y1],
      [x0, y1],
    ];
    const { style, uv } = paneFace(ctx, openings[hole]!, slice, corners);
    const at = ([x, y]: Vec2): Vec3 => panelPoint(panel, x, y);
    ctx.builder.quad(
      style,
      ctx.frame,
      [at(corners[0]), at(corners[1]), at(corners[2]), at(corners[3])],
      uv
    );
  }
}

/** The LOD1 details of an opening as (kind, bottom, height): a door is its leaf and its light. */
function detailsOf(o: Opening): [DetailKind, number, number][] {
  if (o.kind !== 'door') return [[o.kind, o.y, o.height]];
  const leaf = o.leaf ?? o.height;
  const leafPart: [DetailKind, number, number] = ['doorLeaf', o.y, leaf];
  return leaf < o.height ? [leafPart, ['doorTransom', o.y + leaf, o.height - leaf]] : [leafPart];
}

/** One LOD1 detail per opening of a face, or two for a door with a transom light. */
export function placeDetails(ctx: FacadeContext, f: WallFace, openings: readonly Opening[]): void {
  const right = transformDirection(f.panel.uAxis, ctx.frame);
  const outward = transformDirection(f.outward, ctx.frame);
  for (const o of openings) {
    for (const [kind, y, height] of detailsOf(o)) {
      if (ctx.openFrontDoor && f.outward[0] < -0.99 && kind === 'doorLeaf' && o.y < 0.1) continue;
      const origin = transformPoint(panelPoint(f.panel, o.x, y), ctx.frame);
      ctx.details.add(kind, { origin, right, outward, width: o.width, height }, ctx.edgeId);
    }
  }
}

export function emitWall(
  ctx: FacadeContext,
  f: WallFace,
  mesh: WallMesh,
  openings: readonly Opening[],
  recess = 0
): void {
  emitFans(ctx, f.panel, mesh.wall);
  const inset: WallFace =
    recess > 0
      ? {
          ...f,
          panel: {
            ...f.panel,
            origin: [
              f.panel.origin[0] - f.outward[0] * recess,
              f.panel.origin[1] - f.outward[1] * recess,
              f.panel.origin[2] - f.outward[2] * recess,
            ],
          },
        }
      : f;
  emitPanes(ctx, inset.panel, mesh.panes, openings, f.outward);
  placeDetails(ctx, inset, openings);
  if (recess <= 0) return;
  for (const o of openings) {
    const u = f.panel.uAxis,
      v = f.panel.vAxis;
    const edges: readonly (readonly [Vec2, Vec2, Vec3])[] = [
      [[o.x, o.y], [o.x, o.y + o.height], u],
      [
        [o.x + o.width, o.y + o.height],
        [o.x + o.width, o.y],
        [-u[0], -u[1], -u[2]],
      ],
      [[o.x + o.width, o.y], [o.x, o.y], v],
      [
        [o.x, o.y + o.height],
        [o.x + o.width, o.y + o.height],
        [-v[0], -v[1], -v[2]],
      ],
    ];
    for (const [a, b, normal] of edges) {
      orientedFace(
        ctx.builder,
        ctx.styles.wall,
        ctx.frame,
        [
          panelPoint(f.panel, ...a),
          panelPoint(f.panel, ...b),
          panelPoint(inset.panel, ...b),
          panelPoint(inset.panel, ...a),
        ],
        normal
      );
    }
  }
}

/** How far a facade reaches: its width, the eaves and, over a gable end, the ridge. */
export interface FacadeSize {
  readonly width: number;
  readonly eaves: number;
  readonly ridge: number;
}

/**
 * A wall standing from the panel's foot to the eaves, with its gable above when it has one; the
 * openings are in panel coordinates.
 */
export function emitFacade(
  ctx: FacadeContext,
  f: WallFace,
  size: FacadeSize,
  openings: { readonly wall: readonly Opening[]; readonly gable: readonly Opening[] | null },
  recess = 0
): void {
  const { width, eaves, ridge } = size;
  emitWall(ctx, f, meshRectWall(width, eaves, openings.wall.map(holeOf)), openings.wall, recess);
  if (openings.gable)
    emitWall(
      ctx,
      f,
      meshGableWall(width, eaves, ridge, openings.gable.map(holeOf)),
      openings.gable,
      recess
    );
}

/** An opening's outline with a vertex on each side at every cut, so its panes meet the wall edge to edge. */
function holeRing(h: Hole): Vec2[] {
  const right = h.cuts.map((y): Vec2 => [h.x + h.width, y]);
  const left = [...h.cuts].reverse().map((y): Vec2 => [h.x, y]);
  return [
    [h.x, h.y],
    [h.x + h.width, h.y],
    ...right,
    [h.x + h.width, h.y + h.height],
    [h.x, h.y + h.height],
    ...left,
  ];
}

function panesOfHole(h: Hole, hole: number): Pane[] {
  const ys = [h.y, ...h.cuts, h.y + h.height];
  return ys
    .slice(1)
    .map((y1, slice) => ({ hole, slice, x0: h.x, y0: ys[slice]!, x1: h.x + h.width, y1 }));
}

/*
 * Area over longest edge squared below which a triangle counts as a line: twice the mesh audit's
 * limit, so Float32 world positions cannot lift a dropped one's neighbour over it.
 */
const THIN = 2e-5;

function isThin(t: readonly Vec2[]): boolean {
  const [a, b, c] = t as [Vec2, Vec2, Vec2];
  const area = Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])) / 2;
  const d = (p: Vec2, q: Vec2) => (p[0] - q[0]) ** 2 + (p[1] - q[1]) ** 2;
  return area < THIN * Math.max(d(a, b), d(b, c), d(c, a));
}

/**
 * A wall whose outline is not a rectangle, as a shaped gable or a wall notched over a loggia,
 * triangulated by earcut with its openings as holes; the outline is in panel coordinates. Where an
 * opening's corner falls on the line between two other corners, earcut joins the three in a
 * triangle with no width; it covers nothing, so it is left out.
 */
export function emitShapedWall(
  ctx: FacadeContext,
  f: WallFace,
  outline: Ring,
  openings: readonly Opening[]
): void {
  const holes = openings.map(holeOf);
  const wall: Fan[] = triangulateRing(
    ensureCCW(outline),
    holes.map((h) => ensureCCW(holeRing(h)))
  ).filter((t) => !isThin(t));
  emitWall(ctx, f, { wall, panes: holes.flatMap(panesOfHole) }, openings);
}

/** Front of a stepped or spout slab: the storeys meshed like any wall, the shaped gable above the eaves. */
function emitSlabFront(
  ctx: FacadeContext,
  f: WallFace,
  body: Body,
  slab: GableOutline,
  o: HouseOpenings
): void {
  emitWall(ctx, f, meshRectWall(body.half * 2, body.eaves, o.front.map(holeOf)), o.front);
  emitShapedWall(
    ctx,
    f,
    slab.points.slice(2).map((p): Vec2 => [p[2] + body.half, p[1]]),
    o.frontGable
  );
}

/* The body's own front behind a slab: blank but for the shop openings through to the front room. */
function emitBodyFront(
  ctx: FacadeContext,
  f: WallFace,
  size: FacadeSize,
  front: readonly Opening[]
): void {
  const shops = front.filter((o) => o.kind === 'shop').map(holeOf);
  if (shops.length === 0) {
    emitFacade(ctx, f, size, { wall: [], gable: [] });
    return;
  }
  /* One outline to the apex, so the wall meets its gable and the side walls edge to edge. */
  const { width, eaves, ridge } = size;
  const outline: Vec2[] = [
    [0, 0],
    [width, 0],
    [width, eaves],
    [width / 2, ridge],
    [0, eaves],
  ];
  const holes = shops.map((h) => ensureCCW(holeRing(h)));
  emitFans(
    ctx,
    f.panel,
    triangulateRing(ensureCCW(outline), holes).filter((tri) => !isThin(tri))
  );
}

/** A house's front slab, if it has one, and the style of its side walls. */
export interface BodyWalls {
  readonly slab: GableOutline | null;
  readonly side: VertexStyle;
  readonly backRecess?: number;
}

/**
 * All four walls and both gables of the body with their openings in them, and one LOD1 detail
 * per opening. With a slab in front, the body's own front wall is left blank inside it.
 */
export function buildFacades(
  ctx: FacadeContext,
  body: Body,
  openings: HouseOpenings,
  walls: BodyWalls
): void {
  const size = { width: body.half * 2, eaves: body.eaves, ridge: body.ridge };
  if (walls.slab) {
    emitSlabFront(ctx, wallFace([0, 0, -body.half], [-1, 0, 0]), body, walls.slab, openings);
    emitBodyFront(ctx, wallFace([body.x0, 0, -body.half], [-1, 0, 0]), size, openings.front);
  } else {
    emitFacade(ctx, wallFace([0, 0, -body.half], [-1, 0, 0]), size, {
      wall: openings.front,
      gable: openings.frontGable,
    });
  }
  emitFacade(
    ctx,
    wallFace([body.depth, 0, body.half], [1, 0, 0]),
    size,
    { wall: openings.back, gable: openings.backGable },
    walls.backRecess
  );
  const sides = { ...ctx, styles: { ...ctx.styles, wall: walls.side } };
  const side = { ...size, width: body.depth - body.x0 };
  emitFacade(sides, wallFace([body.depth, 0, -body.half], [0, 0, -1]), side, {
    wall: openings.left,
    gable: null,
  });
  emitFacade(sides, wallFace([body.x0, 0, body.half], [0, 0, 1]), side, {
    wall: openings.right,
    gable: null,
  });
}
